import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogContent, DialogActions,
    Button, Alert, CircularProgress,
    Typography, Grid, Box, Paper, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { COLOR_TINTA, crearRenderizadores } from './CampoCaratula';
import { validarFormulario } from '../../../utils/validacionesCaratula';

// Ancho de la "hoja". El Dialog es maxWidth="lg" (1200px), así que sobran ~125px de fondo
// gris a cada lado: es lo que da la sensación de papel apoyado sobre el escritorio, igual
// que la vista de página de Word.
const ANCHO_HOJA = '950px';

const SUBTITULO_POR_DEFECTO =
    'Vista previa del documento. Completá los datos que falten antes de emitirlo.';

/** Keys de CAMPOS que dibuja un layout (cabecera, filas previas y cuerpo, con tablas y apilados). */
function keysDelLayout(hoja) {
    if (!hoja) return [];
    const deFila = (fila) => {
        if (Array.isArray(fila)) return fila;
        if (fila?.apilado) return fila.keys || [];
        if (fila?.tabla) return (fila.tabla.filas || []).flat().filter((c) => typeof c === 'string');
        return [];
    };
    return [
        ...(hoja.cabecera || []).flat(),
        ...(hoja.filasPrevias || []).flatMap(deFila),
        ...(hoja.cuerpo || []).flatMap(deFila),
    ];
}

/**
 * Shell único de las carátulas: el Dialog, el header, el aviso, la hoja A4 y los botones.
 * Lo que cambia entre trámites son los datos, no la presentación, así que cada
 * ModalCaratulaXxx solo aporta su mapa CAMPOS y su layout.
 *
 * Props:
 *  - abierto / cargandoDatos / generando: estado que maneja BotonGenerarCaratula.
 *  - datos: el prefill que devuelve GET /caratula/{tipo}/tramite/{id}/datos.
 *  - campos: mapa key -> { label, tipo, guardable?, resaltada?, nota?, campo? }.
 *  - layout: objeto, o función del formulario en curso para las carátulas con variantes
 *    (Reajuste y Jubilación tienen dos plantillas cada una), con la forma:
 *      { tituloDoc (string o array de líneas), alineacionTitulo?, leyendaDoc?,
 *        cabecera: [[key]], filasPrevias?: [fila], cuerpo: [fila], pie? }
 *    donde `fila` es cualquiera de las formas que acepta renderFila (ver CampoCaratula.js:
 *    keys, apilado, separador o tabla). `pie: null` oculta "SACAR TURNO ANTES".
 *  - onConfirmar(form): genera el documento con el DTO tal cual quedó en pantalla.
 */
export default function HojaCaratula({
    abierto,
    cargandoDatos,
    datos,
    generando,
    titulo,
    subtitulo = SUBTITULO_POR_DEFECTO,
    nombreDocumento = 'la Carátula',
    campos,
    layout,
    onCancelar,
    onConfirmar,
}) {
    const [form, setForm] = useState(null);
    const [error, setError] = useState(null);

    // Se hidrata solo cuando el modal está abierto y ya llegó el prefill; al cerrar se
    // descarta para que la próxima apertura no muestre los datos del trámite anterior.
    // Las dependencias son exactamente [abierto, datos]: agregar el form acá reintroduce
    // el bucle de re-render que documenta CLAUDE.md.
    useEffect(() => {
        if (abierto && datos) {
            setForm(datos);
            setError(null);
        }
        if (!abierto) {
            setForm(null);
        }
    }, [abierto, datos]);

    const handleChange = (clave, valor) => {
        setForm((prev) => ({ ...prev, [clave]: valor }));
    };

    const hoja = form ? (typeof layout === 'function' ? layout(form) : layout) : null;

    // Fechas, mails, claves y domicilios mal formados bloquean el guardado: el backend los
    // rechazaría con 400 (CaratulaDatosClienteService) y el operador perdería lo tipeado. Solo
    // cuentan los renglones visibles: en las carátulas con variantes CAMPOS es la unión de las dos.
    const errores = useMemo(() => {
        const visibles = new Set(keysDelLayout(hoja));
        const camposVisibles = Object.fromEntries(Object.entries(campos || {}).filter(([k]) => visibles.has(k)));
        return validarFormulario(camposVisibles, form);
    }, [campos, form, hoja]);
    const etiquetasConError = Object.keys(errores).map((k) => campos[k]?.label || k);
    const hayErrores = etiquetasConError.length > 0;

    const handleConfirmar = async () => {
        if (hayErrores) {
            toast.warning(`Revisá: ${etiquetasConError.join(', ')}`);
            return;
        }
        setError(null);
        try {
            await onConfirmar(form);
        } catch (err) {
            console.error(`Error generando ${nombreDocumento}:`, err);
            // Los botones adjuntan el mensaje del backend (400 de validación, 404, ...) en
            // `mensajeUsuario`; si no hay, el genérico.
            setError(err?.mensajeUsuario || 'No se pudo generar la Carátula. Intente nuevamente.');
        }
    };

    const { renderCampo, renderFila } = form
        ? crearRenderizadores(campos, form, handleChange, errores)
        : {};

    return (
        <Dialog
            open={abierto}
            onClose={generando ? undefined : onCancelar}
            maxWidth="lg"
            fullWidth
            className="dialog-centrado-contenido"
            PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}
        >
            {/* Header con el gradiente azul del área de Trámites */}
            <Box
                sx={{
                    background: 'var(--primary)',
                    color: 'white',
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        <i className="bi bi-file-earmark-text me-2"></i>{titulo}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {subtitulo}
                    </Typography>
                </Box>
                <IconButton onClick={onCancelar} disabled={generando} sx={{ color: 'white', flexShrink: 0 }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ backgroundColor: '#f8fafc', p: { xs: 2, md: 3 } }}>
                {/* El prefill nunca alcanza para llenar la carátula entera: el aviso deja
                    claro que los renglones en blanco los completa el operador. */}
                <Alert
                    severity="info"
                    icon={<i className="bi bi-info-circle-fill"></i>}
                    sx={{
                        maxWidth: ANCHO_HOJA,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '12px',
                        border: '1px solid #bae6fd',
                        backgroundColor: '#f0f9ff',
                        color: '#0c4a6e',
                        alignItems: 'center',
                        '& .MuiAlert-icon': { color: '#0284c7', alignItems: 'center' },
                    }}
                >
                    <strong>Aviso:</strong> Al generar la carátula se precargan los datos existentes
                    del cliente y del trámite. Complete manualmente los campos en blanco antes de
                    guardar o imprimir.
                </Alert>

                {/* Arriba de la hoja y no al pie: con la hoja A4 entera, al pie quedaba fuera
                    de la vista y el operador no veía por qué no se generó. */}
                {error && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px', maxWidth: ANCHO_HOJA, mx: 'auto' }}>
                        {error}
                    </Alert>
                )}

                {cargandoDatos || !form ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: '48px' },
                            maxWidth: ANCHO_HOJA,
                            mx: 'auto',
                            backgroundColor: '#fff',
                            color: COLOR_TINTA,
                            fontFamily: 'Arial, Helvetica, sans-serif' /* hoja imprimible: tipografía de documento */,
                            border: '1px solid #e2e8f0',
                            borderRadius: '4px',
                            boxShadow: 'var(--shadow-md)',
                        }}
                    >
                        {/* Título del documento: una línea o varias (Declaratoria lleva
                            "DATOS PERSONALES" arriba del nombre del trámite, las dos subrayadas). */}
                        <Box sx={{ textAlign: hoja.alineacionTitulo || 'center', mb: 3 }}>
                            {[].concat(hoja.tituloDoc).map((linea) => (
                                <Typography
                                    key={linea}
                                    sx={{
                                        fontFamily: 'inherit',
                                        fontWeight: 700,
                                        fontSize: '1.15rem',
                                        textDecoration: 'underline',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    {linea}
                                </Typography>
                            ))}
                            {hoja.leyendaDoc && (
                                <Typography sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: '0.95rem', mt: 0.5 }}>
                                    {hoja.leyendaDoc}
                                </Typography>
                            )}
                        </Box>

                        {/* Las cajitas de arriba (son cuadros de texto flotantes en el .docx).
                            Declaratoria y Ganancias no las tienen: sus renglones de arriba van
                            sueltos, en filasPrevias. */}
                        {hoja.cabecera?.length > 0 && (
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {hoja.cabecera.map((keys, idx) => (
                                    <Grid key={idx} size={{ xs: 12, sm: 12 / hoja.cabecera.length }}>
                                        <Box sx={{ border: '1px solid #94a3b8', borderRadius: '2px', p: 1.5, height: '100%' }}>
                                            {keys.map((key) => (
                                                <Box key={key}>{renderCampo(key)}</Box>
                                            ))}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {/* Claves y referencia: van fuera del recuadro, igual que en el papel */}
                        {hoja.filasPrevias?.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                {hoja.filasPrevias.map((keys, idx) => renderFila(keys, `previa-${idx}`))}
                            </Box>
                        )}

                        {/* Bloque principal de datos */}
                        <Box sx={{ border: '1px solid #94a3b8', borderRadius: '2px', p: { xs: 2, md: 3 } }}>
                            {hoja.cuerpo.map((keys, idx) => renderFila(keys, idx))}
                        </Box>

                        {hoja.pie !== null && (
                            <Typography
                                sx={{
                                    fontFamily: 'inherit',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    textAlign: 'center',
                                    color: '#64748b',
                                    mt: 3,
                                }}
                            >
                                {hoja.pie ?? 'SACAR TURNO ANTES'}
                            </Typography>
                        )}
                    </Paper>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0', gap: 1 }}>
                {hayErrores && (
                    <Typography
                        role="alert"
                        sx={{ mr: 'auto', fontSize: '0.85rem', color: '#b45309', display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        <i className="bi bi-exclamation-triangle-fill"></i>
                        Revisá antes de generar: {etiquetasConError.join(', ')}
                    </Typography>
                )}
                <Button
                    onClick={onCancelar}
                    disabled={generando}
                    sx={{
                        backgroundColor: '#fff',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '8px',
                        color: '#475569',
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 3,
                        py: 0.75,
                        '&:hover': { backgroundColor: '#f8fafc', borderColor: '#cbd5e1' },
                    }}
                >
                    Cerrar
                </Button>
                <Button
                    onClick={handleConfirmar}
                    disabled={generando || cargandoDatos || !form || hayErrores}
                    startIcon={generando
                        ? <CircularProgress size={16} sx={{ color: 'white' }} />
                        : <i className="bi bi-download"></i>}
                    sx={{
                        background: 'var(--primary)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 3,
                        py: 0.75,
                        boxShadow: 'var(--shadow-md)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 'var(--shadow-lg)',
                        },
                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af', boxShadow: 'none' },
                    }}
                >
                    Guardar y generar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
